// Circular progress ring showing month-to-date / monthly cap.
// Color shifts amber at 80%, red at 100%.

import type { CapStatus } from "@/services/api/spend";

interface Props {
  status: CapStatus;
  size?: number;
}

export function CapProgressRing({ status, size = 140 }: Props) {
  const radius = (size - 16) / 2;
  const circumference = 2 * Math.PI * radius;
  const ratio = Math.min(1, Math.max(0, status.ratio));
  const offset = circumference * (1 - ratio);

  let strokeColor = "#7d2424"; // oxblood-500 default
  if (status.exceeded) strokeColor = "#b91c1c"; // red-700
  else if (status.warn) strokeColor = "#b45309"; // amber-700

  const used = parseFloat(status.used_usd).toFixed(2);
  const cap = parseFloat(status.cap_usd).toFixed(2);

  return (
    <div className="flex flex-col items-center">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#e8dfce"
          strokeWidth="8"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={strokeColor}
          strokeWidth="8"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ transition: "stroke-dashoffset 0.5s ease" }}
        />
        <text
          x="50%"
          y="48%"
          textAnchor="middle"
          fontSize="22"
          fontFamily="serif"
          fill="#3b2c1f"
        >
          ${used}
        </text>
        <text
          x="50%"
          y="62%"
          textAnchor="middle"
          fontSize="11"
          fill="#998571"
        >
          of ${cap}
        </text>
      </svg>
      {status.exceeded ? (
        <p className="mt-2 text-xs font-medium text-oxblood-600">
          Monthly cap reached
        </p>
      ) : status.warn ? (
        <p className="mt-2 text-xs font-medium text-oxblood-400">
          {Math.round(ratio * 100)}% of cap used
        </p>
      ) : null}
    </div>
  );
}
